import React from 'react';
import {
  Zap,
  Settings,
  Eye,
  Send,
  FileText,
  Maximize2,
  Minimize2,
  ChevronDown,
  ChevronRight,
  ArrowLeft,
} from 'lucide-react';
import { Button, SplitPane, Tabs } from '../../components/ui';
import { useMediaQuery } from '../../hooks/useMediaQuery.js';
import type { TabItem } from '../../components/ui';
import type { WorkflowBuilderStep, ConfigTab } from './WorkflowBuilderTypes.js';
import type { FieldChange } from '../../hooks/useFieldChangeTracker.js';
import { StepListSidebar } from './StepListSidebar.js';
import { StepConfigTab } from './StepConfigTab.js';
import { StepPromptTab } from './StepPromptTab.js';
import { StepOutputTab } from './StepOutputTab.js';
import { StepDebugTab } from './StepDebugTab.js';
import { StepContractTab } from './StepContractTab.js';
import type {
  SaveDebugStepOutputData,
  SaveDebugStepOutputResponse,
  AgentTemplateSummary,
} from '../../generated/api/types.gen.js';
import type { PreviewPromptRole } from './promptPreview.types.js';
import type { MultiStepAgentStream } from '../../hooks/useMultiStepAgentStream.js';

type SaveDebugStepOutputBody = NonNullable<SaveDebugStepOutputData['body']>;

interface StepConfigurationPanelProps {
  // Layout
  stepsCollapsed: boolean;
  stepsExpanded: boolean;
  focusMode: boolean;
  onExpandToggle: () => void;
  onToggle: () => void;
  // Step list
  steps: WorkflowBuilderStep[];
  selectedStep: string | null;
  sel: WorkflowBuilderStep | undefined;
  stepLabels: Map<string, string>;
  stepResults: Record<string, unknown>;
  onSelectStep: (stepId: string | null) => void;
  onAddStep: () => void;
  onRemoveStep: (stepId: string) => void;
  // Tabs
  activeTab: ConfigTab;
  onTabChange: (tab: ConfigTab) => void;
  // Config tab
  roles: AgentTemplateSummary[];
  onUpdateStep: (stepId: string, updates: Partial<WorkflowBuilderStep>) => void;
  // Prompt tab
  promptPreview: {
    systemPrompt: string;
    userPrompt: string;
    agentInput?: string;
    estimatedTokens: number;
    role: PreviewPromptRole;
  } | null;
  promptLoading: boolean;
  instructionMode: 'freetext' | 'template';
  onInstructionModeChange: (mode: 'freetext' | 'template') => void;
  onOpenPromptBuilder: () => void;
  // Debug tab
  templateId: string | undefined;
  isNew: boolean;
  multiStream: MultiStepAgentStream;
  onDebugResult: (stepId: string, result: unknown) => void;
  onFocusToggle: (focused: boolean) => void;
  agentApi?: { saveDebugStepOutput: (params: SaveDebugStepOutputBody) => Promise<SaveDebugStepOutputResponse> };
  // Change tracking
  changes?: Map<string, FieldChange>;
  // Debug statuses
  debugStatuses?: Record<string, 'has_output' | 'drifted'>;
  // Per-step validation warnings (missing template, no output contract, etc.)
  stepWarnings?: Record<string, Array<{ message: string; severity: 'error' | 'warning' }>>;
}

const STEP_TABS: readonly (TabItem & { id: ConfigTab })[] = [
  { id: 'config', label: 'Config', icon: Settings },
  { id: 'prompt', label: 'Prompt', icon: Eye },
  { id: 'output', label: 'Output', icon: Send },
  { id: 'contract', label: 'Contract', icon: FileText },
  { id: 'debug', label: 'Debug', icon: Zap },
];

const isConfigTab = (id: string): id is ConfigTab => STEP_TABS.some((t) => t.id === id);

export const StepConfigurationPanel: React.FC<StepConfigurationPanelProps> = ({
  stepsCollapsed,
  stepsExpanded,
  focusMode,
  onExpandToggle,
  onToggle,
  steps,
  selectedStep,
  sel,
  stepLabels,
  stepResults,
  onSelectStep,
  onAddStep,
  onRemoveStep,
  activeTab,
  onTabChange,
  roles,
  onUpdateStep,
  promptPreview,
  promptLoading,
  instructionMode,
  onInstructionModeChange,
  onOpenPromptBuilder,
  templateId,
  isNew,
  multiStream,
  onDebugResult,
  onFocusToggle,
  agentApi: _agentApi,
  changes,
  debugStatuses,
  stepWarnings,
}) => {
  const wide = useMediaQuery('(min-width: 1100px)');
  const stepList = (
    <StepListSidebar
      steps={steps}
      selectedStep={selectedStep}
      stepLabels={stepLabels}
      stepResults={stepResults}
      debugStatuses={debugStatuses}
      stepWarnings={stepWarnings}
      onSelectStep={onSelectStep}
      onAddStep={onAddStep}
      onRemoveStep={onRemoveStep}
    />
  );
  const configPanel = (
    <div className={`h-full flex-1 min-w-0 flex flex-col overflow-hidden ${!sel ? 'hidden lg:flex' : ''}`}>
      {sel ? (
        <>
          <div className="flex shrink-0 items-center px-2">
            {/* Back button on small screens */}
            <Button variant="ghost" size="sm" className="mr-1 lg:hidden" onClick={() => onSelectStep(null)}>
              <ArrowLeft size={14} /> Steps
            </Button>
            <Tabs
              aria-label="Step sections"
              tabs={STEP_TABS}
              activeTab={activeTab}
              onChange={(id) => {
                if (isConfigTab(id)) onTabChange(id);
              }}
              className="min-w-0 flex-1"
            />
          </div>
          <div className="p-4 flex flex-col flex-1 min-h-0 overflow-auto">
            {activeTab === 'config' && (
              <StepConfigTab
                key={sel.id}
                step={sel}
                steps={steps}
                onUpdateStep={onUpdateStep}
                roles={roles}
                changes={changes}
              />
            )}
            {activeTab === 'prompt' && (
              <StepPromptTab
                key={sel.id}
                step={sel}
                roles={roles}
                promptPreview={promptPreview}
                promptLoading={promptLoading}
                instructionMode={instructionMode}
                onInstructionModeChange={onInstructionModeChange}
                onUpdateStep={onUpdateStep}
                onOpenPromptBuilder={onOpenPromptBuilder}
              />
            )}
            {activeTab === 'output' && <StepOutputTab step={sel} onUpdateStep={onUpdateStep} />}
            {activeTab === 'contract' && (
              <StepContractTab key={sel.id} step={sel} steps={steps} onUpdateStep={onUpdateStep} />
            )}
            {activeTab === 'debug' && (
              <StepDebugTab
                step={sel}
                templateId={templateId}
                isNew={isNew}
                promptPreview={promptPreview}
                multiStream={multiStream}
                onResult={onDebugResult}
                onFocusToggle={onFocusToggle}
                onUpdateStep={onUpdateStep}
                roles={roles}
                dependencies={steps
                  .filter((s) => s.order < sel.order)
                  .map((s) => ({
                    id: s.id.toString(),
                    name: s.name || s.id.toString(),
                    hasOutput: stepResults[s.id.toString()] !== undefined,
                  }))}
                onApproveStep={(stepId) =>
                  onUpdateStep(stepId, {
                    approvalStatus: 'approved',
                    approvedBy: null,
                  })
                }
                onRejectStep={(stepId, _notes) =>
                  onUpdateStep(stepId, {
                    approvalStatus: 'rejected',
                  })
                }
              />
            )}
          </div>
        </>
      ) : (
        <div className="flex items-center justify-center h-full text-muted text-sm">Select a step or click + Add</div>
      )}
    </div>
  );
  return (
    <div
      className={`flex flex-col overflow-hidden rounded-xl border bg-surface ${stepsCollapsed && !focusMode ? 'shrink-0' : 'min-h-120 flex-1'}`}
    >
      <div className="flex shrink-0 items-center justify-between border-b px-4 py-2.5">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-heading">
          <Zap size={14} />
          {focusMode && sel ? sel.name || 'Step config' : 'Steps and configuration'}
        </h3>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            onClick={onExpandToggle}
            aria-label={stepsExpanded ? 'Restore all sections' : 'Expand'}
            title={stepsExpanded ? 'Restore all sections' : 'Expand'}
          >
            {stepsExpanded ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            onClick={onToggle}
            aria-label={stepsCollapsed ? 'Show steps' : 'Collapse steps'}
            title={stepsCollapsed ? 'Show' : 'Collapse'}
          >
            {stepsCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
          </Button>
        </div>
      </div>

      {!stepsCollapsed &&
        (wide && !focusMode ? (
          <SplitPane
            storageKey="workflow-builder-step-split"
            defaultLeft={26}
            minLeft={16}
            maxLeft={50}
            handleLabel="Resize the step list"
            className="min-h-0 flex-1"
            left={stepList}
            right={configPanel}
          />
        ) : (
          <div className="flex flex-col lg:flex-row flex-1 min-h-0">
            {!focusMode && <div className={`${sel ? 'hidden lg:contents' : 'contents'}`}>{stepList}</div>}
            {configPanel}
          </div>
        ))}
    </div>
  );
};
